import { toTableCellText } from '@/shared/lib/table';

export type LabValueSeverity = 'green' | 'red' | 'neutral' | 'unknown';

const NEUTRAL_PHRASES = ['not applicable', 'see comment', 'note', 'pdf', 'report'] as const;

const RED_KEYWORD_TOKENS = new Set([
  'abnormal',
  'high',
  'critical',
  'panic',
  'low',
  'positive',
  'reactive',
  'resistant',
  'nonsusceptible',
  'non-susceptible',
  'intermediate',
  'indeterminate',
  'equivocal',
  'borderline',
  'carrier',
  'unexpected',
  'worse',
  'cytotoxic',
]);

const GREEN_KEYWORD_TOKENS = new Set([
  'wnl',
  'normal',
  'negative',
  'undetected',
  'nonreactive',
  'non-reactive',
  'susceptible',
  'sensitive',
  'expected',
]);

const RED_KEYWORD_PHRASES = [
  'out of range',
  'outside reference range',
  'critical abnormal',
  'critical high',
  'critical low',
  'panic high',
  'panic low',
  'off scale high',
  'off scale low',
  'above range',
  'below range',
  'upper panic limit',
  'lower panic limit',
  'cannot be determined',
  'quality control failure',
  'qc failure',
  'insufficient evidence',
  'anti-complementary',
  'outside threshold',
  'above high threshold',
  'below low threshold',
  'significant change up',
  'significant change down',
] as const;

const GREEN_KEYWORD_PHRASES = [
  'within range',
  'within reference range',
  'in range',
  'within normal limits',
  'not detected',
  'non detected',
  'non-detected',
  'non reactive',
  'synergy susceptible',
  'susceptible dose dependent',
] as const;

function normalizeValue(value: unknown): string {
  return toTableCellText(value, '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function tokenizeWords(value: string): string[] {
  return value.split(/[^a-z0-9-]+/).filter((token) => token !== '');
}

function containsPhrase(value: string, phrase: string): boolean {
  return value.includes(phrase);
}

export function getLabValueSeverity(value: unknown): LabValueSeverity {
  const normalized = normalizeValue(value);
  if (normalized === '') return 'unknown';

  if (NEUTRAL_PHRASES.some((phrase) => containsPhrase(normalized, phrase))) {
    return 'neutral';
  }

  const wordTokens = tokenizeWords(normalized);
  if (
    normalized === 'not detected' ||
    normalized === 'non detected' ||
    normalized === 'non-detected' ||
    normalized === 'non reactive' ||
    normalized === 'non-reactive' ||
    normalized === 'nonreactive'
  ) {
    return 'green';
  }
  if (normalized === 'detected') {
    return 'red';
  }
  if (
    RED_KEYWORD_PHRASES.some((phrase) => containsPhrase(normalized, phrase)) ||
    wordTokens.some((token) => RED_KEYWORD_TOKENS.has(token))
  ) {
    return 'red';
  }

  if (
    GREEN_KEYWORD_PHRASES.some((phrase) => containsPhrase(normalized, phrase)) ||
    wordTokens.some((token) => GREEN_KEYWORD_TOKENS.has(token))
  ) {
    return 'green';
  }

  return 'unknown';
}
